import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request Interceptor ──────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const isVoterEndpoint =
      config.url?.includes('/vote') ||
      config.url?.includes('/ballot') ||
      config.url?.includes('/auth/voter');

    if (isVoterEndpoint) {
      const voterToken = localStorage.getItem('voter_token');
      if (voterToken) config.headers['Authorization'] = `Bearer ${voterToken}`;
    } else {
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) config.headers['Authorization'] = `Bearer ${adminToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response Interceptor ─────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status  = error.response?.status;
    if (status === 401) {
      const isVoterReq = error.config?.url?.includes('/vote') || error.config?.url?.includes('/ballot');
      if (isVoterReq) {
        localStorage.removeItem('voter_token');
        localStorage.removeItem('voter_user');
        window.location.href = '/voter/login';
      } else {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/admin/login';
      }
      toast.error('Sesi habis. Silakan login kembali.');
    } else if (status === 403) {
      toast.error('Akses ditolak.');
    } else if (status >= 500) {
      toast.error('Terjadi kesalahan pada server.');
    }
    return Promise.reject(error);
  }
);

// ── API Methods ───────────────────────────────────────────────
export const authAPI = {
  adminLogin    : (d)     => api.post('/auth/admin/login', d),
  adminRegister : (d)     => api.post('/auth/admin/register', d),
  voterLogin    : (d)     => api.post('/auth/voter/login', d),
  getAdminMe    : ()      => api.get('/auth/admin/me'),
  getVoterMe    : ()      => api.get('/auth/voter/me'),
};

export const electionAPI = {
  list         : (p)      => api.get('/elections', { params: p }),
  get          : (id)     => api.get(`/elections/${id}`),
  create       : (d)      => api.post('/elections', d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update       : (id, d)  => api.put(`/elections/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete       : (id)     => api.delete(`/elections/${id}`),
  updateStatus : (id, s)  => api.patch(`/elections/${id}/status`, { status: s }),
  publish      : (id)     => api.patch(`/elections/${id}/publish`),
  getResults   : (id)     => api.get(`/elections/${id}/results`),
  exportCSV      : (id)     => api.get(`/elections/${id}/results/export/csv`, { responseType: 'blob' }),
  exportPDF      : (id)     => api.get(`/elections/${id}/results/export/pdf`, { responseType: 'blob' }),
  declareWinner  : (id)     => api.patch(`/elections/${id}/declare-winner`),
  getWinners     : (id)     => api.get(`/elections/${id}/winners`),
};

export const positionAPI = {
  listByElection: (eid)        => api.get(`/positions/election/${eid}`),
  create        : (eid, d)     => api.post(`/positions/election/${eid}`, d),
  update        : (id, d)      => api.put(`/positions/${id}`, d),
  delete        : (id)         => api.delete(`/positions/${id}`),
};

export const candidateAPI = {
  listByPosition: (pid)        => api.get(`/candidates/position/${pid}`),
  get           : (id)         => api.get(`/candidates/${id}`),
  create        : (pid, d)     => api.post(`/candidates/position/${pid}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update        : (id, d)      => api.put(`/candidates/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete        : (id)         => api.delete(`/candidates/${id}`),
};

export const voterAPI = {
  list             : (eid, p)  => api.get(`/voters/election/${eid}`, { params: p }),
  addOne           : (eid, d)  => api.post(`/voters/election/${eid}`, d),
  importCSV        : (eid, f)  => {
    const fd = new FormData(); fd.append('file', f);
    return api.post(`/voters/election/${eid}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadTemplate : (eid)     => api.get(`/voters/election/${eid}/template`, { responseType: 'blob' }),
  notifyAll        : (eid, fr) => api.post(`/voters/election/${eid}/notify`, { force: fr }),
  notifySelected   : (eid, ids) => api.post(`/voters/election/${eid}/notify`, { voter_ids: ids }),
  resetKey         : (id) => api.post(`/voters/${id}/reset-key`),
  update           : (id, d)   => api.put(`/voters/${id}`, d),
  delete           : (id)      => api.delete(`/voters/${id}`),
};

export const voteAPI = {
  getBallot : (eid) => api.get(`/vote/ballot/${eid}`),
  getStatus : (eid) => api.get(`/vote/status/${eid}`),
  submit    : (d)   => api.post('/vote/submit', d),
};

export const resultAPI = {
  get             : (eid) => api.get(`/results/${eid}`),
  getParticipation: (eid) => api.get(`/results/${eid}/participation`),
};

export const publicAPI = {
  getResults: (token) => api.get(`/public/${token}/results`),
  getInfo   : (token) => api.get(`/public/${token}/info`),
};

// ── Questionnaire API ─────────────────────────────────────────
export const questionnaireAPI = {
  listByElection: (eid)    => api.get(`/questionnaires/election/${eid}`),
  create        : (eid, d) => api.post(`/questionnaires/election/${eid}`, d),
  update        : (id, d)  => api.put(`/questionnaires/${id}`, d),
  delete        : (id)     => api.delete(`/questionnaires/${id}`),
  reorder       : (eid, d) => api.patch(`/questionnaires/election/${eid}/reorder`, d),
  getAnswers    : (id)     => api.get(`/questionnaires/${id}/answers`),
};

// ── Audit API ─────────────────────────────────────────────────
export const auditAPI = {
  getLogs    : (p) => api.get('/audit/logs',      { params: p }),
  getStats   : ()  => api.get('/audit/stats'),
  getVoterMap: (p) => api.get('/audit/voter-map', { params: p }),
  clearOld   : (d) => api.delete('/audit/logs',   { data: { days: d } }),
};

// ── Helper: Download Blob ─────────────────────────────────────
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
};

export default api;
